import React from 'react';
import SpotlightTutorial from './SpotlightTutorial';
import { useTutorial } from '../hooks/useTutorial';
import { useNavigate, useLocation } from 'react-router-dom';

/**
 * Interactive Comprehensive Tutorial for Tenant
 * Navigates through ALL screens and highlights every key feature
 * 
 * Props:
 *  - forceShow: boolean – when true, show regardless of Firestore state
 *  - onForceComplete: () => void – called when a force-shown tutorial ends
 */
export default function InteractiveComprehensiveTutorial({ forceShow = false, onForceComplete }) {
  const { showTutorial, markTutorialCompleted } = useTutorial('firstTime');
  const navigate = useNavigate();
  const location = useLocation();

  const isVisible = forceShow || showTutorial;

  const goTo = (path) => {
    if (location.pathname !== path) navigate(path);
  };

  const handleComplete = () => {
    markTutorialCompleted();
    if (forceShow && onForceComplete) {
      onForceComplete();
    }
  };

  const allSteps = [
    // ─── WELCOME ───
    {
      title: 'Welkom bij StudyStay! 🎓',
      description: 'We leiden je stap voor stap door alle functies. Je leert hoe je koten zoekt, bezichtigingen plant, contracten bekijkt en meer. Klaar? Laten we beginnen!',
      icon: 'house.fill',
      position: 'center',
      action: () => goTo('/'),
      forceAction: true
    },

    // ─── HOME ───
    {
      title: 'Zoek je ideale kot 🔍',
      description: 'Typ een stad of adres in de zoekbalk om beschikbare koten te vinden. Probeer bijvoorbeeld "Gent" of "Leuven".',
      icon: 'magnifyingglass',
      position: 'bottom',
      targetSelector: '[data-tutorial="search-bar"]',
      action: () => goTo('/'),
      forceAction: true
    },
    {
      title: 'Filter je zoekresultaten 🎯',
      description: 'Klik op "Filters" om te filteren op prijs, oppervlakte, aantal kamers en beschikbaarheid. Zo vind je sneller wat je zoekt.',
      icon: 'sliders-horizontal',
      position: 'bottom',
      targetSelector: '[data-tutorial="filter-button"]'
    },
    {
      title: 'Bekijk een kot 🏠',
      description: 'Klik op een kotkaart om alle details te bekijken: foto\'s, beschrijving, prijs, locatie en voorzieningen.',
      icon: 'house.fill',
      position: 'top',
      targetSelector: '[data-tutorial="property-card"]'
    },
    {
      title: 'Bewaar als favoriet ⭐',
      description: 'Klik op het hartje om een kot op te slaan in je favorieten. Zo kun je het later gemakkelijk terugvinden.',
      icon: 'star.fill',
      position: 'top',
      targetSelector: '[data-tutorial="favorite-button"]'
    },

    // ─── FAVORIETEN ───
    {
      title: 'Favorieten 💛',
      description: 'Al je opgeslagen koten staan hier. Handig om ze later te vergelijken of een bezichtiging aan te vragen.',
      icon: 'star.fill',
      position: 'bottom',
      targetSelector: '[data-tutorial="favorites-nav"]',
      action: () => goTo('/favorites'),
      forceAction: true
    },

    // ─── BERICHTEN ───
    {
      title: 'Berichten 💬',
      description: 'Hier vind je al je chats met verhuurders. Je kunt vanuit een kotdetailpagina een chat starten met "Contact verhuurder". Een rood bolletje toont ongelezen berichten.',
      icon: 'message-circle',
      position: 'bottom',
      targetSelector: '[data-tutorial="messages-nav"]',
      action: () => goTo('/messages'),
      forceAction: true
    },

    // ─── CONTRACTEN ───
    {
      title: 'Contracten 📄',
      description: 'Bekijk al je huurcontracten. Je ziet de status (wacht op ondertekening, getekend, etc.), maandelijkse huur, waarborg en data. Een rood bolletje verschijnt als er een contract wacht op jouw handtekening.',
      icon: 'file-text',
      position: 'bottom',
      targetSelector: '[data-tutorial="contracten-nav"]',
      action: () => goTo('/contracten'),
      forceAction: true
    },

    // ─── BEZICHTIGINGEN ───
    {
      title: 'Bezichtigingen 👁',
      description: 'Hier zie je al je bezichtigingsaanvragen met status: in afwachting, bevestigd, afgewezen. Op de dag van je bezichtiging verschijnt een rood bolletje als je nog geen motivatiebrief hebt ingediend.',
      icon: 'calendar',
      position: 'bottom',
      targetSelector: '[data-tutorial="bezichtigingen-nav"]',
      action: () => goTo('/bezichtigingen'),
      forceAction: true
    },

    // ─── MIJN PAND ───
    {
      title: 'Mijn Pand 🏡',
      description: 'Zodra je een kot huurt, vind je hier alles: je contract, betalingen, meldingen en communicatie met je verhuurder. Dit is je persoonlijke huurportaal.',
      icon: 'building.2.fill',
      position: 'bottom',
      targetSelector: '[data-tutorial="mijn-pand-nav"]',
      action: () => goTo('/mijn-pand'),
      forceAction: true
    },

    // ─── PROPERTY DETAIL ───
    {
      title: 'Kotdetailpagina 📷',
      description: 'Op de detailpagina zie je alles over een kot: foto\'s, beschrijving, voorzieningen, kosten en de locatie op de kaart. Laten we de belangrijkste acties bekijken.',
      icon: 'info',
      position: 'center',
      action: () => {
        const card = document.querySelector('[data-tutorial="property-card"]');
        const propertyId = card?.getAttribute('data-property-id');
        if (propertyId) {
          navigate(`/property/${propertyId}`);
        } else {
          goTo('/');
        }
      },
      forceAction: true
    },
    {
      title: 'Contact verhuurder 💬',
      description: 'Klik hier om direct een chat te starten met de verhuurder. Geen email, alles gebeurt binnen StudyStay zodat je alles kunt terugvinden.',
      icon: 'message-circle',
      position: 'top',
      targetSelector: '[data-tutorial="contact-button"]'
    },
    {
      title: 'Plan een bezichtiging 📅',
      description: 'Vraag een bezichtiging aan door een datum en tijdstip te kiezen. De verhuurder bevestigt of wijst af, en je ziet de status in je Bezichtigingen overzicht.',
      icon: 'calendar',
      position: 'top',
      targetSelector: '[data-tutorial="viewing-button"]'
    },

    // ─── PROFIEL ───
    {
      title: 'Je profiel 👤',
      description: 'Klik op je profielfoto rechtsboven om je persoonlijke gegevens te bewerken: naam, telefoonnummer, adres, opleiding en voorkeuren. Een compleet profiel maakt meer indruk bij verhuurders!',
      icon: 'person.crop.circle.fill',
      position: 'bottom',
      targetSelector: '[data-tutorial="account-nav"]',
      action: () => goTo('/account'),
      forceAction: true
    },

    // ─── DONE ───
    {
      title: 'Je bent klaar! 🎉',
      description: 'Je kent nu alle functies van StudyStay. Veel succes met het vinden van je ideale studentenkamer! Tip: rode bolletjes in de navigatie tonen je waar actie nodig is.',
      icon: 'checkmark.circle.fill',
      position: 'center',
      action: () => goTo('/')
    }
  ];

  if (!isVisible) return null;

  return (
    <SpotlightTutorial
      steps={allSteps}
      isPresented={isVisible}
      onComplete={handleComplete}
    />
  );
}
